/**
 * What is asked of the arrangement beyond the rules arranger's choices: where arranging starts
 * (`ArrangeStartMenu`, drawn by the toolbar ahead of the choices it shuts), the AI arranger's
 * level (`AiArrangerMenu`) and the check, each off until it is turned on.
 *
 * The check is not the AI arranger. It is a search against the book's rules: after the piece is
 * arranged, it moves or leaves out the arranger's own notes where the checker flags a chord, gives
 * a player sounding nothing a note of a chord whose spacing it flags, and hands the changed score
 * back only where the checker then finds fewer faults. Starting from the tune it is not offered:
 * nothing of the score's other parts is kept for it to work among. Starting from the note model it
 * is always made, on the model's notes, the AI arranger is shut, and the model's add-ons
 * (`ArrangeHints`) are offered: what it is told of the score beyond the tune and how far
 * (`ArrangeFollow`), as `FOLLOW_ON` has it until switched, and sent only when starting from the
 * model. The source's notes,
 * the tune's, a counter-melody's and a line doubled are never touched, and a player the AI arranger rests is
 * never given a note. Both sit apart from the
 * rules arranger's choices, so an answer in words, which sets those, leaves them as they were.
 * The wishes (`ArrangeWishes`) close the strip: the key, the tempo, how the players play and the ease,
 * which an answer in words sets.
 */

import {
  followSent,
  type AiLevel,
  type ArrangeFollow,
  type ArrangeStart,
  type ArrangeWishes as Wished,
  type Seat,
} from "@app/shared-types";
import AiArrangerMenu, { AI_NAMES } from "./AiArrangerMenu";
import ArrangeHints, { FOLLOW_ON, saidOf } from "./ArrangeHints";
import { anewOf, CHECK_SAID, MODEL_AI_SHUT } from "./ArrangeStartMenu";
import ArrangeWishes, { NO_WISHES, wishesSaid, wishesSent, type Wishes } from "./ArrangeWishes";
import { Divider } from "./Chrome";

export interface Review {
  readonly start: ArrangeStart;
  readonly ai: AiLevel;
  readonly check: boolean;
  /** How the note model is set to the score; it waits, unsent, while it is not started from. */
  readonly told: ArrangeFollow;
  /** The key, how the players play and the ease (`ArrangeWishes`). */
  readonly wishes: Wishes;
}

export const NO_REVIEW: Review = {
  start: "score",
  ai: "off",
  check: false,
  told: FOLLOW_ON,
  wishes: NO_WISHES,
};

/** A review as a run's request carries it. */
export type Requested = Pick<Review, "start" | "ai" | "check"> &
  ReturnType<typeof followSent> &
  Wished;

/**
 * The review a run is sent with: the check is never asked for when starting from the tune,
 * starting from the note model takes no AI arranger level and makes the check unasked, only
 * starting from the model sends its add-ons, and the wishes are sent as `wishesSent` has them.
 */
export function requested({ start, ai, check, told, wishes }: Review): Requested {
  const wished = wishesSent(wishes, start);
  if (start === "model") return { start, ai: "off", check: false, ...followSent(told), ...wished };
  return { start, ai, check: check && start === "score", hints: [], strength: 1, ...wished };
}

const CHECK_TITLE =
  "After arranging, move or leave out the arranger's own notes (those the harmony or the AI arranger added) where the book's rules flag a chord, give a waiting player a note of a chord whose spacing is flagged (never one the AI arranger rests), and keep the change only where the checker then finds fewer faults. Never a note of the source's, the tune's, a counter-melody's or a line doubled. The faults before and after are shown under the score.";

/** What the folded strip says of the review, after the players; empty when nothing is on. */
export function reviewed(review: Review): string {
  const { start, ai, check } = requested(review);
  const from =
    start === "tune"
      ? " · from the tune"
      : start === "model"
        ? ` · the note model${saidOf(review.told)}`
        : "";
  return `${from}${ai === "off" ? "" : ` · AI arranger ${AI_NAMES[ai].label}`}${check || start === "model" ? " · checked" : ""}${wishesSaid(review.wishes, start)}`;
}

export default function ArrangeReview({
  review,
  roster,
  seats,
  onReview,
}: {
  readonly review: Review;
  readonly roster: readonly string[];
  readonly seats: readonly Seat[];
  onReview(review: Review): void;
}): JSX.Element {
  const anew = anewOf(review.start);
  const model = anew === "model";
  return (
    <>
      <Divider />
      <AiArrangerMenu
        value={model ? "off" : review.ai}
        shut={model ? MODEL_AI_SHUT : undefined}
        onPick={(ai) => onReview({ ...review, ai })}
      />
      <label
        title={anew === null ? CHECK_TITLE : CHECK_SAID[anew]}
        className={`inline-flex h-7 items-center gap-1.5 text-[11px] text-shell-text ${anew === null ? "cursor-pointer hover:text-shell-lit" : "opacity-40"}`}
      >
        <input
          type="checkbox"
          checked={model || (review.check && anew === null)}
          disabled={anew !== null}
          onChange={(event) => onReview({ ...review, check: event.target.checked })}
          className="h-3.5 w-3.5 accent-shell-live"
        />
        Check
      </label>
      {model ? (
        <ArrangeHints follow={review.told} onFollow={(told) => onReview({ ...review, told })} />
      ) : null}
      <Divider />
      <ArrangeWishes
        wishes={review.wishes}
        start={review.start}
        roster={roster}
        seats={seats}
        onWishes={(wishes) => onReview({ ...review, wishes })}
      />
    </>
  );
}
